import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Spin, Table, message } from "antd";
import {
  BarChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  ReloadOutlined,
  RiseOutlined,
  TeamOutlined,
  TrophyOutlined,
} from "@ant-design/icons";

/*
 * This page uses ONLY its own stylesheet.
 * Every class is prefixed with "sad-" so exam.css (or any other
 * stylesheet) cannot affect it, and it cannot affect other pages.
 */
import "./assessment-dashboard.css";

const EXAM_API_BASE =
  process.env.REACT_APP_EXAM_API_BASE_URL || "http://localhost:5000/api/exams";

const EMPTY_DASHBOARD = {
  summary: {
    total_exams: 0,
    active_exams: 0,
    inactive_exams: 0,
    total_questions: 0,
    total_attempts: 0,
    employees_attempted: 0,
    submitted_attempts: 0,
    started_attempts: 0,
    submitted_today: 0,
    average_score: 0,
    completion_percentage: 0,
  },
  score_distribution: {
    score_0_39: 0,
    score_40_59: 0,
    score_60_79: 0,
    score_80_100: 0,
  },
  departments: [],
  units: [],
  monthly_activity: [],
  activity_trend: [],
  recent_attempts: [],
};

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/* ---------------------------------------------------------------
   Helpers
   --------------------------------------------------------------- */
const parseJsonSafely = async (response) => {
  const raw = await response.text();

  if (!raw) {
    return {
      status: response.ok,
      data: null,
      message: response.ok ? "Request completed" : "Request failed",
    };
  }

  try {
    return JSON.parse(raw);
  } catch {
    return {
      status: false,
      data: null,
      message: `Server returned an invalid response (${response.status})`,
    };
  }
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const clampPercent = (value) => Math.min(100, Math.max(0, toNumber(value)));

const formatPercent = (value) =>
  `${toNumber(value).toFixed(1).replace(/\.0$/, "")}%`;

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTrendDate = (value) => {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString([], { day: "2-digit", month: "short" });
};

const normalizeDashboard = (result) => {
  const data = result?.data || {};
  const monthlyRaw = Array.isArray(data.monthly_activity)
    ? data.monthly_activity
    : [];

  const monthlyMap = new Map(
    monthlyRaw.map((item) => [toNumber(item.month_number), item])
  );

  const monthlyActivity = MONTHS.map((month, index) => {
    const row = monthlyMap.get(index + 1);
    return {
      month,
      month_number: index + 1,
      attempts: toNumber(row?.attempts),
      submitted: toNumber(row?.submitted),
      average_score: toNumber(row?.average_score),
    };
  });

  const asArray = (value) => (Array.isArray(value) ? value : []);

  return {
    summary: { ...EMPTY_DASHBOARD.summary, ...(data.summary || {}) },
    score_distribution: {
      ...EMPTY_DASHBOARD.score_distribution,
      ...(data.score_distribution || {}),
    },
    departments: asArray(data.departments),
    units: asArray(data.units),
    monthly_activity: monthlyActivity,
    activity_trend: asArray(data.activity_trend),
    recent_attempts: asArray(data.recent_attempts),
  };
};

/* ---------------------------------------------------------------
   Small components
   --------------------------------------------------------------- */
const CardHead = ({ title, subtitle, extra }) => (
  <div className="sad-card-head">
    <div>
      <h3>{title}</h3>
      {subtitle ? <p>{subtitle}</p> : null}
    </div>
    {extra || null}
  </div>
);

const KpiCard = ({ icon, label, value, note, tone }) => (
  <div className={`sad-kpi sad-tone-${tone}`}>
    <div className="sad-kpi-main">
      <span className="sad-kpi-icon">{icon}</span>
      <div className="sad-kpi-value">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
    <div className="sad-kpi-note">{note}</div>
  </div>
);

const UnitCard = ({ item }) => {
  const attempts = toNumber(item.attempts);
  const submitted = toNumber(item.submitted);
  const inProgress = toNumber(item.in_progress);

  const submittedPercentage = clampPercent(
    item.submitted_percentage ??
      item.completion_percentage ??
      (attempts > 0 ? (submitted / attempts) * 100 : 0)
  );

  const inProgressPercentage = clampPercent(
    item.in_progress_percentage ??
      (attempts > 0 ? (inProgress / attempts) * 100 : 0)
  );

  return (
    <div className="sad-unit">
      <div className="sad-unit-head">
        <strong>{item.unit_name || `Unit ${item.unit_code || "-"}`}</strong>
        <span className="sad-text-attempts">
          <i className="sad-dot sad-bg-attempts" />
          {attempts} Attempts
        </span>
      </div>

      <div className="sad-unit-body">
        <div className="sad-unit-row">
          <div className="sad-unit-row-copy">
            <span>Submitted - {submitted}</span>
            <strong className="sad-text-submitted">
              {formatPercent(submittedPercentage)}
            </strong>
          </div>
          <div className="sad-progress">
            <i
              className="sad-bg-submitted"
              style={{ width: `${submittedPercentage}%` }}
            />
          </div>
        </div>

        <div className="sad-unit-row">
          <div className="sad-unit-row-copy">
            <span>In Progress - {inProgress}</span>
            <strong className="sad-text-progress">
              {formatPercent(inProgressPercentage)}
            </strong>
          </div>
          <div className="sad-progress">
            <i
              className="sad-bg-progress"
              style={{ width: `${inProgressPercentage}%` }}
            />
          </div>
        </div>

        <div className="sad-unit-meta">
          <span>{toNumber(item.employees)} Employees</span>
          <span>
            Avg Score <strong>{formatPercent(item.average_score)}</strong>
          </span>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------
   Page
   --------------------------------------------------------------- */
const AssessmentDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${EXAM_API_BASE}/dashboard`);
      const result = await parseJsonSafely(response);

      if (!response.ok || !result?.status) {
        message.error(result?.message || "Unable to load assessment dashboard");
        setDashboard(EMPTY_DASHBOARD);
        return;
      }

      setDashboard(normalizeDashboard(result));
      setLastRefreshed(new Date());
    } catch (error) {
      console.error("Assessment dashboard load error:", error);
      message.error("Unable to connect to the exam server");
      setDashboard(EMPTY_DASHBOARD);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const {
    summary,
    departments,
    units,
    monthly_activity: monthlyActivity,
    activity_trend: activityTrend,
    recent_attempts: recentAttempts,
  } = dashboard;

  const completion = clampPercent(summary.completion_percentage);

  const departmentActivity = useMemo(
    () =>
      [...departments]
        .sort((a, b) => toNumber(b.attempts) - toNumber(a.attempts))
        .slice(0, 10),
    [departments]
  );

  const scoreDistribution = useMemo(() => {
    const d = dashboard.score_distribution || {};
    return [
      { key: "0-39", range: "0–39", label: "Needs Support", tone: "red", count: toNumber(d.score_0_39) },
      { key: "40-59", range: "40–59", label: "Basic", tone: "orange", count: toNumber(d.score_40_59) },
      { key: "60-79", range: "60–79", label: "Good", tone: "blue", count: toNumber(d.score_60_79) },
      { key: "80-100", range: "80–100", label: "Excellent", tone: "green", count: toNumber(d.score_80_100) },
    ];
  }, [dashboard.score_distribution]);

  const maxScoreCount = useMemo(
    () => Math.max(1, ...scoreDistribution.map((item) => item.count)),
    [scoreDistribution]
  );

  const monthlyMax = useMemo(
    () =>
      Math.max(
        1,
        ...monthlyActivity.map((item) =>
          Math.max(toNumber(item.attempts), toNumber(item.submitted))
        )
      ),
    [monthlyActivity]
  );

  const trendMax = useMemo(
    () =>
      Math.max(
        1,
        ...activityTrend.map((item) =>
          Math.max(toNumber(item.attempts), toNumber(item.submitted))
        )
      ),
    [activityTrend]
  );

  const topUnit = useMemo(() => {
    if (!units.length) return null;
    return [...units].sort(
      (a, b) => toNumber(b.average_score) - toNumber(a.average_score)
    )[0];
  }, [units]);

  const topDepartment = useMemo(() => {
    if (!departments.length) return null;
    return [...departments].sort(
      (a, b) => toNumber(b.average_score) - toNumber(a.average_score)
    )[0];
  }, [departments]);

  const recentColumns = [
    {
      title: "Employee",
      key: "employee",
      width: 190,
      render: (_, record) => (
        <strong className="sad-cell-strong">
          {record.full_name || record.username || "Employee"}
        </strong>
      ),
    },
    {
      title: "Department",
      key: "department",
      width: 160,
      render: (_, record) => record.department_name || "Department name not found",
    },
    {
      title: "Unit",
      key: "unit",
      width: 130,
      render: (_, record) => record.unit_name || "Unit",
    },
    {
      title: "Exam",
      dataIndex: "exam_name",
      key: "exam_name",
      width: 220,
      render: (value) => <strong className="sad-cell-strong">{value || "-"}</strong>,
    },
    {
      title: "Attempt",
      dataIndex: "attempt_no",
      key: "attempt_no",
      width: 78,
      align: "center",
    },
    {
      title: "Score",
      key: "score",
      width: 85,
      align: "center",
      render: (_, record) => (
        <strong className="sad-score">{formatPercent(record.percentage)}</strong>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      align: "center",
      render: (status) => {
        const normalized = String(status || "").toLowerCase();
        const tone =
          normalized === "submitted"
            ? "submitted"
            : normalized
            ? "progress"
            : "neutral";

        return (
          <span className={`sad-status sad-status-${tone}`}>
            {normalized || "-"}
          </span>
        );
      },
    },
    {
      title: "Submitted Date",
      key: "submitted",
      width: 165,
      render: (_, record) =>
        formatDateTime(record.submitted_at || record.started_at),
    },
  ];

  return (
    <div className="sad-page">
      {/* ---------- Header ---------- */}
      <header className="sad-header">
        <div>
          <h1>Employee Assessment Dashboard</h1>
          <p>Assessment activity, completion and performance overview</p>
        </div>

        <div className="sad-header-actions">
          <span>
            Last refreshed{" "}
            <strong>
              {lastRefreshed
                ? lastRefreshed.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "-"}
            </strong>
          </span>
          <Button
            className="sad-refresh-btn"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={loadDashboard}
          >
            Refresh
          </Button>
        </div>
      </header>

      {loading && !lastRefreshed ? (
        <div className="sad-loading">
          <Spin size="large" />
          <span>Loading assessment dashboard...</span>
        </div>
      ) : (
        <>
          {/* ---------- KPI ---------- */}
          <div className="sad-kpi-grid">
            <KpiCard
              tone="brand"
              icon={<FileDoneOutlined />}
              label="Active Exams"
              value={toNumber(summary.active_exams)}
              note={`${toNumber(summary.total_exams)} total exams`}
            />
            <KpiCard
              tone="blue"
              icon={<TeamOutlined />}
              label="Employees Attempted"
              value={toNumber(summary.employees_attempted)}
              note="Unique employees"
            />
            <KpiCard
              tone="attempts"
              icon={<BarChartOutlined />}
              label="Total Attempts"
              value={toNumber(summary.total_attempts)}
              note={`${toNumber(summary.submitted_today)} submitted today`}
            />
            <KpiCard
              tone="submitted"
              icon={<CheckCircleOutlined />}
              label="Submitted"
              value={toNumber(summary.submitted_attempts)}
              note={`${formatPercent(completion)} completion`}
            />
            <KpiCard
              tone="progress"
              icon={<ClockCircleOutlined />}
              label="In Progress"
              value={toNumber(summary.started_attempts)}
              note="Open attempts"
            />
            <KpiCard
              tone="blue"
              icon={<TrophyOutlined />}
              label="Average Score"
              value={formatPercent(summary.average_score)}
              note={
                topDepartment
                  ? `Top: ${
                      topDepartment.department_name ||
                      `Dept ${topDepartment.department_id}`
                    }`
                  : "Submitted exams"
              }
            />
            <KpiCard
              tone="submitted"
              icon={<RiseOutlined />}
              label="Completion"
              value={formatPercent(completion)}
              note="Submitted / attempts"
            />
            <KpiCard
              tone="slate"
              icon={<FileTextOutlined />}
              label="Total Questions"
              value={toNumber(summary.total_questions)}
              note={`${toNumber(summary.inactive_exams)} inactive exams`}
            />
          </div>

          {/* ---------- Main analytics ---------- */}
          <div className="sad-main-grid">
            {/* Completion */}
            <section className="sad-card sad-completion">
              <CardHead
                title="Employee Assessment Completion %"
                subtitle="Overall employee assessment completion"
              />

              <div className="sad-gauge-wrap">
                <div
                  className="sad-gauge"
                  style={{ "--sad-completion": completion }}
                >
                  <div className="sad-gauge-center">
                    <strong>{formatPercent(completion)}</strong>
                    <span>Completed</span>
                  </div>
                </div>
              </div>

              <div className="sad-completion-stats">
                <div>
                  <span>Total Attempts</span>
                  <strong className="sad-text-attempts">
                    {toNumber(summary.total_attempts)}
                  </strong>
                </div>
                <div>
                  <span>Submitted</span>
                  <strong className="sad-text-submitted">
                    {toNumber(summary.submitted_attempts)}
                  </strong>
                </div>
              </div>

              <div className="sad-card-footer">
                <span>Average Score</span>
                <strong>{formatPercent(summary.average_score)}</strong>
              </div>
            </section>

            {/* Monthly */}
            <section className="sad-card sad-monthly">
              <CardHead
                title="Monthly Employee Assessment Progress"
                subtitle="Monthly employee attempts and submissions"
              />

              <div className="sad-monthly-scroll">
                <div className="sad-monthly-chart">
                  <div className="sad-chart-grid">
                    {[100, 75, 50, 25].map((line) => (
                      <i key={line} style={{ bottom: `${line}%` }} />
                    ))}
                  </div>

                  <div className="sad-month-bars">
                    {monthlyActivity.map((item) => {
                      const attempts = toNumber(item.attempts);
                      const submitted = toNumber(item.submitted);

                      return (
                        <div className="sad-month" key={item.month_number}>
                          <div className="sad-month-bar-area">
                            <i
                              className="sad-bar sad-bg-attempts"
                              style={{ height: `${(attempts / monthlyMax) * 100}%` }}
                              title={`${item.month}: ${attempts} attempts`}
                            />
                            <i
                              className="sad-bar sad-bg-submitted"
                              style={{ height: `${(submitted / monthlyMax) * 100}%` }}
                              title={`${item.month}: ${submitted} submitted`}
                            />
                          </div>
                          <span>{item.month}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="sad-legend">
                <span>
                  <i className="sad-bg-attempts" />
                  Attempts
                </span>
                <span>
                  <i className="sad-bg-submitted" />
                  Submitted
                </span>
              </div>
            </section>

            {/* Module-wise */}
            <section className="sad-card sad-modules">
              <CardHead
                title="Module-wise Progress Details"
                subtitle="Employee attempts and submissions"
              />

              <div className="sad-module-list">
                {departmentActivity.length ? (
                  departmentActivity.map((item) => (
                    <div className="sad-module-row" key={item.department_id}>
                      <strong>
                        {item.department_name || `Department ${item.department_id}`}
                      </strong>
                      <div className="sad-module-values">
                        <span className="sad-text-attempts">
                          {toNumber(item.attempts)}
                          <small>Attempts</small>
                        </span>
                        <span className="sad-text-submitted">
                          {toNumber(item.submitted)}
                          <small>Submitted</small>
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No department activity"
                  />
                )}
              </div>

              <div className="sad-card-footer">
                <span>Departments</span>
                <strong>{departments.length}</strong>
              </div>
            </section>
          </div>

          {/* ---------- Units ---------- */}
          <section className="sad-card sad-units">
            <CardHead
              title="Unit-wise Performance Details"
              subtitle="Employee completion, pending attempts and average score by unit"
              extra={
                topUnit ? (
                  <span className="sad-top-unit">
                    Top Unit:{" "}
                    <strong>{topUnit.unit_name || topUnit.unit_code}</strong>
                    {" · "}
                    {formatPercent(topUnit.average_score)}
                  </span>
                ) : null
              }
            />

            {units.length ? (
              <div className="sad-unit-grid">
                {units.map((item) => (
                  <UnitCard key={`${item.unit_code}-${item.unit_name}`} item={item} />
                ))}
              </div>
            ) : (
              <div className="sad-empty">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No unit performance data"
                />
              </div>
            )}
          </section>

          {/* ---------- Score + Last 7 days ---------- */}
          <div className="sad-secondary-grid">
            <section className="sad-card sad-score">
              <CardHead
                title="Score Distribution"
                subtitle="Submitted attempts by score range"
              />

              <div className="sad-score-chart">
                {scoreDistribution.map((item) => {
                  const height =
                    item.count > 0
                      ? Math.max(8, (item.count / maxScoreCount) * 100)
                      : 0;

                  return (
                    <div className={`sad-score-col sad-band-${item.tone}`} key={item.key}>
                      <strong>{item.count}</strong>
                      <div className="sad-score-track">
                        <i style={{ height: `${height}%` }} />
                      </div>
                      <span>{item.range}</span>
                      <small>{item.label}</small>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="sad-card sad-week">
              <CardHead title="Last 7 Days" subtitle="Recent assessment activity" />

              <div className="sad-week-scroll">
                <div className="sad-week-list">
                  {activityTrend.length ? (
                    activityTrend.map((item) => {
                      const attempts = toNumber(item.attempts);
                      const submitted = toNumber(item.submitted);

                      return (
                        <div className="sad-week-row" key={item.date}>
                          <strong className="sad-week-date">
                            {formatTrendDate(item.date)}
                          </strong>

                          <div className="sad-week-bars">
                            <div className="sad-week-track">
                              <i
                                className="sad-bg-attempts"
                                style={{ width: `${(attempts / trendMax) * 100}%` }}
                              />
                            </div>
                            <div className="sad-week-track">
                              <i
                                className="sad-bg-submitted"
                                style={{ width: `${(submitted / trendMax) * 100}%` }}
                              />
                            </div>
                          </div>

                          <span className="sad-week-value sad-text-attempts">
                            {attempts}
                            <small>attempts</small>
                          </span>
                          <span className="sad-week-value sad-text-submitted">
                            {submitted}
                            <small>submitted</small>
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No recent activity"
                    />
                  )}
                </div>
              </div>

              <div className="sad-legend">
                <span>
                  <i className="sad-bg-attempts" />
                  Attempts
                </span>
                <span>
                  <i className="sad-bg-submitted" />
                  Submitted
                </span>
              </div>
            </section>
          </div>

          {/* ---------- Recent table ---------- */}
          <section className="sad-card sad-recent">
            <CardHead
              title="Recent Employee Assessment Details"
              subtitle="Latest 10 employee assessment attempts"
            />

            <div className="sad-table">
              <Table
                rowKey="attempt_id"
                columns={recentColumns}
                dataSource={recentAttempts}
                pagination={false}
                size="middle"
                scroll={{ x: 1140 }}
                locale={{ emptyText: "No recent assessments found" }}
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AssessmentDashboardPage;
